import {useState, type Ref} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {CREATE_ACCOUNT_STEPS, FORM_NAMES} from 'pages/Auth/constants';
import type {FormTypes} from 'pages/Auth/types';
import {AccountForm} from 'pages/Auth/components/AccountForm';
import {AuthIdentityChip} from 'pages/Auth/components/AuthIdentityChip';
import {GoogleSignInButton} from 'pages/Auth/components/SocialLoginButtons';
import {STEP_SUBTITLE_COMPONENTS} from 'pages/Auth/components/stepSubtitle';
import {CardBackButton} from 'pages/SearchReservation/components/CardBackButton';
import {PropertyLinkShell} from 'pages/SearchReservation/components/PropertyLinkShell';
import {StepHeading} from './StepParts';

const SIGN_UP_STEP_INDEX: Partial<Record<CREATE_ACCOUNT_STEPS, number>> = {
  [CREATE_ACCOUNT_STEPS.email]: 1,
  [CREATE_ACCOUNT_STEPS.password]: 2,
  [CREATE_ACCOUNT_STEPS.checkEmailCode]: 3,
};

const SIGN_UP_STEPS_TOTAL = Object.keys(SIGN_UP_STEP_INDEX).length;

type Props = {
  columnRef: Ref<HTMLDivElement>;
  onSignedIn: () => void;
  onBack: () => void;
};

function EmailStep({columnRef, onSignedIn, onBack}: Props) {
  const {t} = useTranslation();
  const [accountStep, setAccountStep] = useState(CREATE_ACCOUNT_STEPS.email);
  const [isBusy, setIsBusy] = useState(false);

  const methods = useForm<FormTypes>({
    defaultValues: {email: '', password: '', email_code: ''},
  });
  const {watch, resetField} = methods;
  const email = watch(FORM_NAMES.email) ?? '';
  const stepCounter = {
    step: SIGN_UP_STEP_INDEX[accountStep] ?? 1,
    total: SIGN_UP_STEPS_TOTAL,
  };

  const handleRestartFromEmail = () => {
    resetField(FORM_NAMES.password);
    resetField(FORM_NAMES.email_code);
    setAccountStep(CREATE_ACCOUNT_STEPS.email);
  };

  const handleSignedIn = () => {
    setIsBusy(true);
    onSignedIn();
  };

  const {title, subtitle} = (() => {
    switch (accountStep) {
      case CREATE_ACCOUNT_STEPS.login:
        return {
          title: t('auth.create_account.welcome_back'),
          subtitle: t('auth.enter_password_subtitle'),
        };
      case CREATE_ACCOUNT_STEPS.ssoOnly:
        return {
          title: t('auth.continue_with_google'),
          subtitle: t('auth.google_only_subtitle'),
        };
      case CREATE_ACCOUNT_STEPS.password:
        return {
          title: t('auth.create_account.password_title'),
          subtitle: (
            <Trans
              i18nKey="auth.create_account.new_account_subtitle"
              values={stepCounter}
              components={STEP_SUBTITLE_COMPONENTS}
            />
          ),
        };
      case CREATE_ACCOUNT_STEPS.checkEmailCode:
        return {
          title: t('auth.create_account.check_your_inbox'),
          subtitle: (
            <Trans
              i18nKey="auth.create_account.code_subtitle"
              values={{email, ...stepCounter}}
              components={STEP_SUBTITLE_COMPONENTS}
            />
          ),
        };
      default:
        return {
          title: t('instant_check_in.email_title'),
          subtitle: t('instant_check_in.email_lead'),
        };
    }
  })();

  return (
    <PropertyLinkShell columnRef={columnRef}>
      <CardBackButton
        onClick={
          accountStep === CREATE_ACCOUNT_STEPS.email ? onBack : handleRestartFromEmail
        }
        disabled={isBusy}
      />

      <StepHeading lead={subtitle}>{title}</StepHeading>

      {accountStep === CREATE_ACCOUNT_STEPS.ssoOnly ? (
        <div className="flex flex-col gap-3">
          <AuthIdentityChip
            email={email}
            onChange={handleRestartFromEmail}
            disabled={isBusy}
          />
          <GoogleSignInButton onSuccessAuth={handleSignedIn} disabled={isBusy} />
        </div>
      ) : (
        <FormProvider {...methods}>
          <AccountForm
            step={accountStep}
            onConfirm={setAccountStep}
            onGoBack={handleRestartFromEmail}
            onSuccessAuth={handleSignedIn}
            isBusy={isBusy}
          />
        </FormProvider>
      )}
    </PropertyLinkShell>
  );
}

export {EmailStep};
